"use client";

import { PROV_NAV_MOBILPARNING_MS } from "@/lib/klient-intervall";
import { useEffect, useState } from "react";

// Small poll used by Nav while an elev is on /prov-elev/[passId] to resolve
// the one fact that isn't derivable from the URL: is the elev inside an
// ACTIVE pass right now, and does that pass allow mobilparning. The header
// is the same as everywhere else (Erik 2026-09-15); the only difference is
// that the mobile-pairing button answers with a "förbjudet"-popup when the
// pass forbids it. On the resultat page there is no active pass → normal.
//
// Deliberately its own light poll rather than reusing ProvVakt's — ProvVakt
// can't be reached from here without turning it into a shared context, and
// mobilparningTillaten is frozen at pass-start (never changes mid-exam), so a
// slow, low-cost interval is enough. Only runs while `enabled` (i.e. Nav is
// actually on a /prov-elev/ path), so it never adds load anywhere else.

export interface ProvNavLage {
  /** Eleven är inne i ett aktivt (startat) pass just nu. */
  aktivt: boolean;
  /** Passet tillåter mobilparning (bara meningsfullt när aktivt). */
  mobilTillaten: boolean;
}

export function useProvNavMobilparning(enabled: boolean): ProvNavLage {
  const [lage, setLage] = useState<ProvNavLage>({ aktivt: false, mobilTillaten: true });

  useEffect(() => {
    if (!enabled) {
      setLage({ aktivt: false, mobilTillaten: true });
      return;
    }

    let cancelled = false;
    let timer: number | null = null;

    async function tick() {
      try {
        const res = await fetch("/api/prov-elev/aktivt", { cache: "no-store" });
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as {
          aktivt?: { mobilparningTillaten?: boolean; lasplattaTillaten?: boolean };
        };
        setLage({
          aktivt: data.aktivt != null,
          // QR-knappen är gemensam för mobil och läsplatta — förbjuden bara
          // när provet stänger båda; prime/upload/heartbeat avgör sedan per enhet.
          mobilTillaten: Boolean(data.aktivt?.mobilparningTillaten || data.aktivt?.lasplattaTillaten),
        });
      } catch {
        // Tyst — nästa tick försöker igen.
      } finally {
        if (!cancelled) timer = window.setTimeout(tick, PROV_NAV_MOBILPARNING_MS);
      }
    }

    tick();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [enabled]);

  return lage;
}
